// src/components/layout/MobileHeaderProvider.tsx
'use client';

import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';

type AnyComp = React.ComponentType | null;

type Ctx = {
  mobileHeader: AnyComp;
  setMobileHeader: (c: AnyComp) => void;
  disableMobileHeader: boolean;
  setDisableMobileHeader: (disable: boolean) => void;
};

const MobileHeaderCtx = createContext<Ctx | null>(null);

export const MobileHeaderProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [mobileHeader, _setMobileHeader] = useState<AnyComp>(null);
  const [disableMobileHeader, setDisableMobileHeader] = useState(false);

  // 包一層，避免 React 把元件函式當 updater 調用而「執行」它
  const setMobileHeader = (c: AnyComp) => _setMobileHeader(() => c);

  const value = useMemo(
    () => ({ mobileHeader, setMobileHeader, disableMobileHeader, setDisableMobileHeader }),
    [mobileHeader, disableMobileHeader]
  );

  return <MobileHeaderCtx.Provider value={value}>{children}</MobileHeaderCtx.Provider>;
};

/** 頁面或 layout 指定「本頁的手機 Header」；第二參數 disable=true 時，會直接關閉手機 Header 切換 */
export function useSetMobileHeader(comp: AnyComp, disable = false) {
  const ctx = useContext(MobileHeaderCtx);

  useEffect(() => {
    if (!ctx) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[MobileHeaderProvider] useSetMobileHeader called outside provider.');
      }
      return;
    }

    if (disable) {
      ctx.setDisableMobileHeader(true);
      return () => ctx.setDisableMobileHeader(false);
    }

    ctx.setMobileHeader(comp);
    return () => ctx.setMobileHeader(null);
  }, [ctx, comp, disable]);
}

/** 在 layout 或頁面統一開/關手機 Header 切換（mount 開、unmount 還原） */
export function useDisableMobileHeader(disable = true) {
  const ctx = useContext(MobileHeaderCtx);

  useEffect(() => {
    if (!ctx) return;
    ctx.setDisableMobileHeader(disable);
    return () => ctx.setDisableMobileHeader(false);
  }, [ctx, disable]);
}

/** 在 layout 或頁面「完全關閉桌機＋手機 Header」 */
export function useDisableAllHeader(disable = true) {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (disable) {
      document.body.dataset.disableAllHeader = 'true';
    }
    return () => {
      delete document.body.dataset.disableAllHeader;
    };
  }, [disable]);
}

/** 取當前應顯示的手機 Header（若已被 disable，回傳 null） */
export function useCurrentMobileHeader() {
  const ctx = useContext(MobileHeaderCtx);
  return ctx?.disableMobileHeader ? null : (ctx?.mobileHeader ?? null);
}
